import { useEffect, useCallback, useMemo, useRef } from "react";
import { store as accBootStore } from "../../Lit/acc-boot/acc-boot.js";
import type { TreeNodeData } from "../TreeViewDataLayer";

interface UseTreeViewSearchProps {
  aboData: TreeNodeData | null;
  expandPathToNode: (targetId: string) => void;
  setSelectedAbo: (abo: TreeNodeData | null) => void;
  setSelectedNodeId: (id: string | null) => void;
  panToNode: (targetId: string, shouldZoom?: boolean) => void;
  /** ABO number to restore selection/highlight for once the tree finishes loading -
   *  e.g. a search made in list view before switching to tree view, which this
   *  component has no memory of since it fully unmounts on view toggle. */
  initialSelectedAboNumber?: string | null;
}

interface UseTreeViewSearchReturn {
  allNodes: TreeNodeData[];
}

/**
 * Hook to handle tree view search functionality including flattening tree data
 * and handling external search events from Lit components
 */
export const useTreeViewSearch = ({
  aboData,
  expandPathToNode,
  setSelectedAbo,
  setSelectedNodeId,
  panToNode,
  initialSelectedAboNumber,
}: UseTreeViewSearchProps): UseTreeViewSearchReturn => {
  // Ref keeps the latest panToNode so the setTimeout callback always calls
  // the version that closes over the post-expansion aboData, not a stale one.
  const panToNodeRef = useRef(panToNode);
  useEffect(() => {
    panToNodeRef.current = panToNode;
  }, [panToNode]);

  // Ref holds the pending pan timer so rapid searches cancel the previous one
  // and component unmount clears it to avoid calling state setters after unmount.
  const panTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (panTimerRef.current !== null) {
        clearTimeout(panTimerRef.current);
      }
    };
  }, []);

  const flattenTree = useCallback((node: TreeNodeData): TreeNodeData[] => {
    const nodes: TreeNodeData[] = [node];
    node.downlines?.forEach((child) => nodes.push(...flattenTree(child)));
    return nodes;
  }, []);

  const allNodes = useMemo(() => (aboData ? flattenTree(aboData) : []), [aboData, flattenTree]);

  useEffect(() => {
    if (allNodes.length > 0) {
      const searchData = allNodes.map((node) => ({
        aboNumber: node.abo,
        abo: node.abo,
        affAbo: `${node.aff}-${node.abo}`,
        profile: {
          name: node.aboAttributes.aboName,
          localName: node.aboAttributes.aboName,
        },
      }));
      // @ts-expect-error - accBootStore is a legacy Lit component without proper types
      accBootStore.setState({ searchData });
    }
  }, [allNodes]);

  const selectByAboNumber = useCallback(
    (aboNumber: string) => {
      if (!allNodes.length) return;

      const foundNode = allNodes.find((node) => node.abo.toString() === aboNumber);
      if (foundNode) {
        expandPathToNode(aboNumber);
        setSelectedAbo(foundNode);
        setSelectedNodeId(aboNumber);
        // Cancel any previous pending pan, then schedule a new one.
        // Delay so the canvas redraws with expanded ancestors before panning.
        // Use the ref so we always call the post-expansion closure of panToNode.
        // shouldZoom=true resets zoom to 1 so the selected node is clearly visible.
        if (panTimerRef.current !== null) {
          clearTimeout(panTimerRef.current);
        }
        panTimerRef.current = setTimeout(() => {
          panTimerRef.current = null;
          panToNodeRef.current(aboNumber, true);
        }, 50);
      }
    },
    [allNodes, expandPathToNode, setSelectedAbo, setSelectedNodeId],
  );

  const handleExternalSearch = useCallback(
    (event: CustomEvent<{ record: { abo?: number; aboNumber?: number | string } }>) => {
      // rwlosSearchData records (built from raw LosInode API data) carry the
      // ABO number in `abo`, not `aboNumber` — reading only `aboNumber` here
      // meant this handler silently no-op'd for every real search selection.
      const aboNumber = (event.detail?.record?.abo ?? event.detail?.record?.aboNumber)?.toString();
      if (aboNumber) selectByAboNumber(aboNumber);
    },
    [selectByAboNumber],
  );

  useEffect(() => {
    document.addEventListener("acc-los-data-find", handleExternalSearch as EventListener);
    return () => document.removeEventListener("acc-los-data-find", handleExternalSearch as EventListener);
  }, [handleExternalSearch]);

  // Restore a selection made before this component mounted (e.g. list view's
  // search, or a previous tree view session before toggling to list and back).
  // Only fires once per distinct target - re-selecting on every allNodes change
  // would fight the user's own subsequent clicks/searches within this session.
  const restoredAboNumberRef = useRef<string | null>(null);
  useEffect(() => {
    if (initialSelectedAboNumber && allNodes.length > 0 && restoredAboNumberRef.current !== initialSelectedAboNumber) {
      restoredAboNumberRef.current = initialSelectedAboNumber;
      selectByAboNumber(initialSelectedAboNumber);
    }
  }, [initialSelectedAboNumber, allNodes, selectByAboNumber]);

  return { allNodes };
};
